import React, { useState, useEffect } from "react";
import { getFormConfig, getFieldsForStep, getStepTitle, getTotalSteps, validateStep } from "../services/formConfigService";
import "./DynamicForm.css";

/**
 * DynamicForm2 Component
 * Renders form fields dynamically based on configuration
 * Used for service-specific registration (from service pages)
 * First step is always service selection, then personal details
 */
const DynamicForm2 = ({
  formType = "registration_provider_with_service",
  onSubmit,
  submitButtonText = "Submit",
  showStepper = true,
  initialData = {},
  apiConfig = null, // Optional API config for fetching dynamic configs
  preSelectedService = null // Pre-selected service when coming from service page
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [formConfig, setFormConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const totalSteps = formConfig ? formConfig.steps.length : getTotalSteps(formType);
  const currentFields = formConfig 
    ? (formConfig.steps.find(s => s.stepNumber === currentStep)?.fields || [])
    : getFieldsForStep(formType, currentStep);
  const stepTitle = formConfig
    ? (formConfig.steps.find(s => s.stepNumber === currentStep)?.stepTitle || '')
    : getStepTitle(formType, currentStep);

  // Fetch form configuration from API if provided
  useEffect(() => {
    const fetchFormConfig = async () => {
      if (apiConfig) {
        setIsLoading(true);
        try {
          const response = await fetch(apiConfig.url, {
            headers: {
              'Authorization': `Bearer ${apiConfig.token}`,
              'Content-Type': 'application/json',
            }
          });
          const data = await response.json();
          if (data.success && data.data) {
            setFormConfig(data.data);
          }
        } catch (error) {
          console.error("Error fetching form config:", error);
          // Fallback to default config
          setFormConfig(getFormConfig(formType));
        } finally {
          setIsLoading(false);
        }
      } else {
        setFormConfig(getFormConfig(formType));
      }
    };

    fetchFormConfig();
  }, [formType, apiConfig]);

  // Pre-select service when preSelectedService is provided
  useEffect(() => {
    if (preSelectedService && formData.service_categories === undefined) {
      setFormData(prev => ({
        ...prev,
        service_categories: [preSelectedService]
      }));
    }
  }, [preSelectedService, formData.service_categories]);

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };

  const handleNext = () => {
    const { isValid, errors: stepErrors } = validateStep(currentFields, formData);
    if (!isValid) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setErrors({});
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all steps
    let allErrors = {};
    let isFormValid = true;

    if (formConfig) {
      formConfig.steps.forEach(step => {
        const { isValid, errors: stepErrors } = validateStep(step.fields, formData);
        if (!isValid) {
          allErrors = { ...allErrors, ...stepErrors };
          isFormValid = false;
        }
      });
    } else {
      for (let step = 1; step <= totalSteps; step++) {
        const fields = getFieldsForStep(formType, step);
        const { isValid, errors: stepErrors } = validateStep(fields, formData);
        if (!isValid) {
          allErrors = { ...allErrors, ...stepErrors };
          isFormValid = false;
        }
      }
    }

    if (!isFormValid) {
      setErrors(allErrors);
      return;
    }

    setErrors({});
    onSubmit(formData);
  };

  const renderField = (field) => {
    const fieldError = errors[field.name];
    const value = formData[field.name] || (field.type === 'checkbox' || field.type === 'radio' ? [] : '');

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
      case 'tel':
      case 'number':
      case 'date':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <input
              type={field.type}
              className={`form-control custom-input ${fieldError ? 'is-invalid' : ''}`}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
            {fieldError && <div className="invalid-feedback">{fieldError}</div>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <textarea
              rows="3"
              className={`form-control custom-input ${fieldError ? 'is-invalid' : ''}`}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
            {fieldError && <div className="invalid-feedback">{fieldError}</div>}
          </div>
        );

      case 'select':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <select
              className={`form-select custom-input ${fieldError ? 'is-invalid' : ''}`}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            >
              <option value="" disabled>Select {field.label.toLowerCase()}</option>
              {field.options?.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {fieldError && <div className="invalid-feedback">{fieldError}</div>}
          </div>
        );

      case 'checkbox':
        const currentValues = Array.isArray(value) ? value : [];
        // Checkbox group (e.g., service categories, availability)
        if (field.options && field.options.length > 0) {
          return (
            <div key={field.name} className="mb-3">
              <label className="form-label">
                {field.label} {field.required && <span className="required">*</span>}
              </label>
              {field.options.map((option) => {
                const isChecked = currentValues.includes(option.value);
                return (
                  <div className="form-check" key={option.value}>
                    <input
                      type="checkbox"
                      className={`form-check-input ${fieldError ? 'is-invalid' : ''}`}
                      checked={isChecked}
                      onChange={(e) => {
                        const updatedValues = isChecked
                          ? currentValues.filter((v) => v !== option.value)
                          : [...currentValues, option.value];
                        handleInputChange(field.name, updatedValues);
                      }}
                    />
                    <label className="form-check-label">{option.label}</label>
                  </div>
                );
              })}
              {fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
            </div>
          );
        }
        // Single checkbox (e.g., terms & conditions)
        const isChecked = currentValues.includes(field.name);
        return (
          <div key={field.name} className="form-check mb-3">
            <input
              type="checkbox"
              className={`form-check-input ${fieldError ? 'is-invalid' : ''}`}
              checked={isChecked}
              onChange={(e) => {
                const updatedValues = e.target.checked
                  ? [...currentValues, field.name]
                  : currentValues.filter((v) => v !== field.name);
                handleInputChange(field.name, updatedValues);
              }}
            />
            <label className="form-check-label">
              {field.label} {field.required && <span className="required">*</span>}
            </label>
            {fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
          </div>
        );

      case 'radio':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <div className="selection-grid">
              {field.options?.map(option => (
                <label className="select-card" key={option.value}>
                  <input
                    type="radio"
                    name={field.name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {fieldError && <div className="text-danger mt-1">{fieldError}</div>}
          </div>
        );

      case 'file':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <input
              type="file"
              className="form-control custom-input"
              placeholder={field.placeholder}
              onChange={(e) => handleInputChange(field.name, e.target.files[0])}
            />
            {fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className="text-center py-5">Loading form...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="dynamic-form">
      {showStepper && (
        <div className="stepper-progress mb-4">
          <div className="d-flex justify-content-between mb-2">
            <span className="steps_txt">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="steps_p">
              {Math.round((currentStep / totalSteps) * 100)}%
            </span>
          </div>
          <div className="progress">
            <div
              className="progress-bar"
              style={{
                width: `${(currentStep / totalSteps) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      )}

      {stepTitle && showStepper && (
        <h4 className="step-title mb-4">{stepTitle}</h4>
      )}

      <div className="form-fields">
        {currentFields.map(field => renderField(field))}
      </div>

      <div className="d-flex justify-content-between mt-4">
        {currentStep > 1 ? (
          <button
            type="button"
            className="btn back_btn w-100 me-3"
            onClick={handleBack}
          >
            Back
          </button>
        ) : (
          <div></div>
        )}

        {currentStep < totalSteps ? (
          <button
            type="button"
            className="btn login-btn w-100"
            onClick={handleNext}
          >
            Next
          </button>
        ) : (
          <button
            type="submit"
            className="btn login-btn w-100"
          >
            {submitButtonText}
          </button>
        )}
      </div>
    </form>
  );
};

export default DynamicForm2;